'use client'

import { AlertCircle, CloudUpload, X } from 'lucide-react'

import Image from 'next/image'
import type React from 'react'
import { useCallback, useRef } from 'react'

import { DocxIcon, MicrosoftExcel, PdfIcon, PptIcon } from '@/components/icons'
import { cn } from '@/lib/cn'
import type { FileType } from '@/types/fileType'
import type { UploadProgressData } from '@/types/uploadProgress'
import {
  MIMETYPE_MAPPER,
  mimeTypeToExtensions,
} from '@/utils/mimeTypeToExtension'
import { Progress } from '../progress'
import { useToast } from '../toast'

export type DropzoneProps = {
  files: FileType[]
  onChange: (files: FileType[]) => void
  className?: string
  mimeType?: string[]
  error?: string
  description?: string
  maxFile?: number
  maxSizeInMB?: number
  previewTitleClassName?: string
  progress?: UploadProgressData | null
  /** Called when the user removes a file via the dropzone's own remove button. */
  onRemove?: (file: FileType, index: number) => void
  /** Called with the files that passed mime type and size validation. */
  onDropAccepted?: (files: FileType[]) => void
  /** Called with the files rejected for mime type or size — before the toast fires. */
  onDropRejected?: (files: FileType[]) => void
}

const Dropzone = ({
  files,
  onChange,
  className,
  mimeType,
  error,
  description = 'Arraste uma imagem aqui ou clique para buscar',
  maxFile = 3,
  maxSizeInMB = 3,
  previewTitleClassName,
  progress,
  onRemove,
  onDropAccepted,
  onDropRejected,
}: DropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { toast } = useToast()

  const handleFiles = useCallback(
    (fileList: FileList) => {
      const validFiles: FileType[] = []
      const invalidFiles: FileType[] = []
      const maxSize = maxSizeInMB * 1024 * 1024 // Converter MB para bytes

      const currentFileCount = files.length
      const filesRemaining = maxFile - currentFileCount

      if (filesRemaining <= 0) {
        toast({
          title: 'Limite de arquivos atingido',
          description: 'Você atingiu o limite de arquivos permitidos',
          variant: 'destructive',
          icon: AlertCircle,
        })
        return
      }

      const filesToProcess = Array.from(fileList).slice(0, filesRemaining)

      for (let i = 0; i < filesToProcess.length; i++) {
        const uploadedFile = filesToProcess[i]
        if (uploadedFile.size > maxSize) {
          invalidFiles.push({ file: uploadedFile })
          continue
        }

        if (!mimeType) {
          validFiles.push({ file: uploadedFile })
          continue
        }

        const uploadedMimeType = uploadedFile.type
        const isValidMimeType = mimeType?.some((type) => {
          if (type.endsWith('/*')) {
            const [baseType] = type.split('/')
            return uploadedMimeType.startsWith(baseType)
          }

          return uploadedMimeType === type
        })

        if (isValidMimeType) {
          validFiles.push({ file: uploadedFile })
        } else {
          invalidFiles.push({ file: uploadedFile })
        }
      }

      onChange([...files, ...validFiles])

      if (validFiles.length) {
        onDropAccepted?.(validFiles)
      }

      if (invalidFiles.length) {
        onDropRejected?.(invalidFiles)
        const errorMessage = `Arquivos inválidos\n${invalidFiles
          .map(({ file }) => file?.name)
          .join('\n')}`
        toast({
          title: 'Arquivos inválidos',
          description: errorMessage,
          variant: 'destructive',
          icon: AlertCircle,
        })
      }
    },
    [
      maxSizeInMB,
      files,
      maxFile,
      onChange,
      toast,
      mimeType,
      onDropAccepted,
      onDropRejected,
    ],
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
    },
    [],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const { files } = event.dataTransfer
      handleFiles(files)
    },
    [handleFiles],
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target

      if (files) {
        handleFiles(files)
      }
    },
    [handleFiles],
  )

  const handleButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  const handlePreview = (file: FileType) => {
    if ('preview' in file) {
      return file.preview
    }

    return URL.createObjectURL(file.file)
  }

  const removeFile = (index: number) => {
    const removedFile = files[index]
    onChange(files.filter((_, i) => i !== index))
    onRemove?.(removedFile, index)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const FileIcon = ({ file }: { file: FileType }) => {
    const iconType = MIMETYPE_MAPPER[
      file.file?.type as keyof typeof MIMETYPE_MAPPER
    ] || [file.file?.type]

    const isImage = iconType[0]?.startsWith('image') || !!file.preview

    if (isImage) {
      return (
        <Image
          src={handlePreview(file) || ''}
          alt="preview"
          width={80}
          height={80}
          className="size-20 rounded-md object-cover transition duration-300 ease-in-out group-hover:blur-sm"
        />
      )
    }

    switch (iconType[0]) {
      case 'doc':
        return (
          <div className="flex size-16">
            <DocxIcon />
          </div>
        )
      case 'docx':
        return (
          <div className="flex size-16">
            <DocxIcon />
          </div>
        )
      case 'xls':
        return (
          <div className="flex size-16">
            <MicrosoftExcel color="#000" className="size-16" />
          </div>
        )
      case 'xlsx':
        return (
          <div className="flex size-16">
            <MicrosoftExcel color="#000" className="size-16" />
          </div>
        )
      case 'csv':
        return (
          <div className="flex size-16">
            <MicrosoftExcel color="#000" className="size-16" />
          </div>
        )
      case 'ppt':
        return (
          <div className="flex size-16">
            <PptIcon />
          </div>
        )
      case 'pptx':
        return (
          <div className="flex size-16">
            <PptIcon />
          </div>
        )
      case 'pdf':
        return (
          <div className="flex size-16">
            <PdfIcon />
          </div>
        )
      default:
        return <div> Não foi possível identificar o tipo de arquivo</div>
    }
  }

  const isMimeTypeImage = mimeType?.some((type) => type.startsWith('image'))

  return (
    <div className="flex w-full flex-col gap-2" data-testid="dropzone">
      <div
        className={cn(
          'border-2 border-dashed hover:cursor-pointer hover:border-muted-foreground/50 rounded-md',
          error && 'border-destructive/50 hover:border-destructive',
          progress?.status === 'error' &&
            'border-destructive/50 hover:border-destructive',
          className,
        )}
        onClick={handleButtonClick}
      >
        {progress && progress.percentage > 0 && (
          <div className="flex flex-col items-center justify-center gap-2 space-y-2 px-2 py-12">
            {progress.status === 'error' ? (
              <div className="flex flex-col items-center justify-center gap-2">
                <span className="text-sm text-system-state-error">
                  Erro ao fazer upload do arquivo{' '}
                  <span className="font-bold">{progress.fileName}</span>
                </span>
                <span className="text-xs text-system-state-error">
                  Um erro foi encontrado na linha{' '}
                  {progress.currentError?.lineNumber}
                </span>
              </div>
            ) : (
              <p className="text-sm text-system-text-primary">
                <span>
                  Fazendo upload do arquivo{' '}
                  <span className="font-bold">{progress.fileName}</span>
                </span>
              </p>
            )}
            <div className="flex w-8/12 flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn(
                    'max-w-40 truncate text-xs text-system-text-secondary',
                    progress.status === 'error' && 'text-system-state-error',
                  )}
                >
                  Nome: {progress.currentItem}
                </span>
                <span
                  className={cn(
                    'text-xs text-system-text-secondary',
                    progress.status === 'error' && 'text-system-state-error',
                  )}
                >
                  {progress.processed} de {progress.total}
                </span>
              </div>
              <Progress
                value={progress.percentage}
                max={progress.total}
                className="h-3 w-full bg-system-border-secondary"
                indicatorClassName={cn(
                  'bg-system-state-success',
                  progress.status === 'error' && 'bg-system-state-error',
                )}
              />
            </div>
          </div>
        )}

        {!progress && (
          <div
            className="flex flex-col items-center justify-center gap-4 space-y-2 px-2 py-12"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-2 text-center">
                <CloudUpload size={36} className="text-system-primary-icon" />
                <span className="font-normal text-system-text-primary">
                  {description}
                </span>
                <span className="font-inter text-sm font-normal text-system-text-secondary">
                  É permitido no max. {maxFile} arquivos e no max. {maxSizeInMB}
                  MB
                </span>
                {mimeType && (
                  <span className="text-xs">
                    Extensões permitidas{' '}
                    {mimeTypeToExtensions(mimeType).join(', ')}
                  </span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={mimeType?.join(', ')}
                onChange={handleFileInputChange}
                className="hidden"
                multiple
              />
            </div>
            {files?.length > 0 && (
              <div
                className={cn(
                  'flex flex-wrap gap-2',
                  !isMimeTypeImage && 'flex-col gap-1',
                )}
              >
                {files?.map((file, index) => (
                  <div
                    key={index}
                    className={
                      isMimeTypeImage
                        ? 'relative'
                        : 'flex min-w-0 items-center justify-between rounded bg-muted px-3 py-2'
                    }
                  >
                    {isMimeTypeImage ? (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFile(index)
                          }}
                          className="absolute right-0 top-0 rounded-full bg-black/80 p-1 text-white"
                        >
                          <X />
                        </button>
                        {(file.file?.type || file.preview) && (
                          <FileIcon file={file} />
                        )}
                      </>
                    ) : (
                      <>
                        <span
                          className={cn(
                            'truncate text-sm font-medium text-system-text-primary',
                            previewTitleClassName,
                          )}
                        >
                          {file?.file?.name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFile(index)
                          }}
                          className="ml-2 rounded-full bg-black/70 p-1 text-white hover:bg-black"
                          aria-label="Remover arquivo"
                        >
                          <X />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <span className="text-sm font-medium text-destructive">{error}</span>
      )}
    </div>
  )
}
Dropzone.displayName = 'Dropzone'

export { Dropzone }
